import { useEffect, useId } from "react";
import { Search } from "lucide-react";
import { cx } from "../../lib/cx";
import { dsControl, dsFont } from "./styles";

export type FilterTabItem = {
  id: string;
  label: string;
  count?: number;
  testId?: string;
};

type TabsProps = {
  tabs: FilterTabItem[];
  value: string;
  onChange: (id: string) => void;
  /** Accessible name for the tablist. */
  label: string;
};

/**
 * Filter pills. The selected tab is an ink pill; the count sits beside the label.
 * Callers pass the labels and the counts.
 */
export function FilterTabs({ tabs, value, onChange, label }: TabsProps) {
  return (
    <div
      role="tablist"
      aria-label={label}
      data-ds=""
      data-testid="ds-filter-tabs"
      className={cx(dsFont, "flex flex-wrap items-center gap-2")}
    >
      {tabs.map((tab) => {
        const selected = tab.id === value;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            data-testid={tab.testId ?? `ds-tab-${tab.id}`}
            className={cx(
              dsControl,
              "inline-flex items-center gap-[7px] rounded-ds-pill px-3.5 py-[7px] text-ds-body",
              selected
                ? "bg-ds-teal font-semibold text-white"
                : "bg-transparent font-normal text-ds-ink-2 hover:bg-ds-hover",
            )}
            onClick={() => onChange(tab.id)}
          >
            {tab.label}
            {tab.count != null ? (
              <span className={cx("text-ds-label font-normal tabular-nums", selected && "opacity-70")}>
                {tab.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

type SearchProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  testId?: string;
  /** Focus the field when `/` is pressed outside another field. */
  shortcut?: boolean;
  className?: string;
};

/** Pill search. Glass-strong, 36px. Optional `/` shortcut. */
export function ListSearch({
  value,
  onChange,
  placeholder,
  label,
  testId,
  shortcut = true,
  className,
}: SearchProps) {
  const inputId = useId();

  useEffect(() => {
    if (!shortcut) return;
    function onKey(event: KeyboardEvent) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) return;
      event.preventDefault();
      document.getElementById(inputId)?.focus();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [inputId, shortcut]);

  // Reference search is at least 260px. The extra `ch` keeps a long placeholder
  // (EN "Search name, email or domain", TR "Ad, e-posta veya alan adı ara")
  // inside the field instead of clipping it when the row is tight.
  const minWidth = `max(260px, calc(${placeholder.length}ch + 4.25rem))`;

  return (
    <label
      data-ds=""
      style={{ minWidth }}
      className={cx(
        dsFont,
        "flex h-9 w-max max-w-full shrink-0 items-center gap-2 rounded-ds-pill bg-ds-glass-strong px-3.5 text-ds-body text-ds-ink-3",
        className,
      )}
    >
      <Search className="size-3.5 shrink-0" aria-hidden />
      <input
        id={inputId}
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={label}
        data-testid={testId ?? "ds-list-search"}
        className="m-0 w-auto min-w-0 flex-1 border-0 bg-transparent p-0 font-ds text-ds-body font-normal text-ds-ink outline-none placeholder:text-ds-ink-3 [font-family:inherit]"
        onChange={(event) => onChange(event.target.value)}
      />
      {shortcut ? (
        <kbd className="shrink-0 font-ds text-ds-badge font-normal text-ds-ink-3">/</kbd>
      ) : null}
    </label>
  );
}
